/**
 * AddSectionDialog
 * Dialog for selecting and adding a new section to a page.
 */

import { useState } from 'react'
import * as LucideIcons from 'lucide-react'
import { Loader2, Search } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useAddSection } from '@/hooks/use-page-sections'
import {
  SECTION_REGISTRY,
  getSectionCategories,
  getSectionsByCategory,
  type SectionCategory,
  type SectionTypeDefinition,
} from '@limo/shared'
import { cn } from '@/lib/utils'

interface AddSectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  pageId: string
  accountId: string
  insertAtIndex?: number
}

export function AddSectionDialog({
  open,
  onOpenChange,
  pageId,
  accountId,
  insertAtIndex,
}: AddSectionDialogProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const addSection = useAddSection()

  // Get categories for tabs
  const categories = getSectionCategories()

  // Filter sections based on search and category
  const getFilteredSections = (): SectionTypeDefinition[] => {
    let sections =
      selectedCategory === 'all'
        ? Object.values(SECTION_REGISTRY)
        : getSectionsByCategory(selectedCategory as SectionCategory)

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      sections = sections.filter(
        (s) =>
          s.name.toLowerCase().includes(query) ||
          s.description.toLowerCase().includes(query)
      )
    }

    return sections
  }

  const filteredSections = getFilteredSections()

  // Handle section selection
  const handleSelectSection = async (sectionType: string) => {
    try {
      await addSection.mutateAsync({
        accountId,
        pageId,
        sectionType,
        insertAtIndex,
      })
      onOpenChange(false)
      setSearchQuery('')
      setSelectedCategory('all')
    } catch (err) {
      // Error handled by hook
      console.error('Failed to add section:', err)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Section</DialogTitle>
          <DialogDescription>
            Choose a section type to add to your page. You can customize the
            content after adding.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search sections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Category Tabs */}
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
          <TabsList className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="all" className="text-xs">
              All
            </TabsTrigger>
            {categories.map((category) => (
              <TabsTrigger
                key={category.id}
                value={category.id}
                className="text-xs"
              >
                {category.name}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={selectedCategory} className="mt-4">
            <ScrollArea className="h-[400px] pr-4">
              {filteredSections.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No sections match your search.
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {filteredSections.map((section) => (
                    <SectionCard
                      key={section.type}
                      section={section}
                      onClick={() => handleSelectSection(section.type)}
                      isLoading={
                        addSection.isPending &&
                        addSection.variables?.sectionType === section.type
                      }
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

// Section card within the dialog
interface SectionCardProps {
  section: SectionTypeDefinition
  onClick: () => void
  isLoading: boolean
}

function SectionCard({ section, onClick, isLoading }: SectionCardProps) {
  // Get the icon component from Lucide
  const IconComponent =
    (LucideIcons as Record<string, React.ComponentType<{ className?: string }>>)[
      section.icon
    ] || LucideIcons.FileText

  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg border text-left transition-all',
        'hover:border-primary hover:bg-muted/50',
        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed'
      )}
    >
      <div className="rounded-lg bg-primary/10 p-2 shrink-0">
        {isLoading ? (
          <Loader2 className="h-5 w-5 text-primary animate-spin" />
        ) : (
          <IconComponent className="h-5 w-5 text-primary" />
        )}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-sm truncate">{section.name}</h4>
          {section.supportsMultiple && (
            <Badge variant="outline" className="text-[10px] shrink-0">
              Multi
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {section.description}
        </p>
      </div>
    </button>
  )
}
